import { Collection } from 'firestorter';
import Collections from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';

class WeeklyCheckin extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.WEEKLY_CHECKIN}/${id}`, opts);
  }

  get checkInData() {
    return this.data.checkInData || {};
  }

  get habitData() {
    return this.data.habitData || {};
  }

  get questions() {
    return this.data.questions || {};
  }

  get updated() {
    return this.data.updated?.toDate();
  }

  get submittedAt() {
    return this.data.submittedAt?.toDate();
  }

  get user() {
    return this.data.user;
  }

  get coach() {
    return this.data.coach;
  }

  get checkInId() {
    return this.data.checkInId;
  }

  /**
   * The weekday the client checks in on, 0 = Sunday. CalorieIntakeChart rotates the week to start
   * here, and falls back to Sunday without it — which draws a Wednesday check-in week as Monday
   * first, putting the last two days of the week ahead of its first.
   */
  get checkInDay() {
    return this.data.checkInDay;
  }

  get mealsSummary() {
    return this.data.mealsSummary || {};
  }

  get workoutData() {
    return this.data.workoutData || [];
  }

  get userMealsSubmitted() {
    return this.data.userMealsSubmitted;
  }

  get progressionData() {
    return this.data.progressionData;
  }

  /**
   * One coach's check-in panels for a client, newest first. Each coach has their own panel on
   * every check-in, so an unscoped read would draw the other coach's weights and answers too.
   */
  static async getByUserAndCoach(userId, coachId) {
    const weeklyCheckinCollection = new Collection(Collections.WEEKLY_CHECKIN, {
      createDocument: (src, opts) => new WeeklyCheckin(src.id, opts),
      query: (ref) => ref
        .where('user', '==', userId)
        .where('coach', '==', coachId)
        .orderBy('submittedAt', 'desc'),
    });
    await weeklyCheckinCollection.fetch();
    return weeklyCheckinCollection;
  }

  /**
   * Every coach's panels for a client, newest first. For the client's own numbers, which every
   * coach's panel repeats — a coach-scoped read would miss the history of a previous coach.
   *
   * @param {string} userId - The client.
   * @param {number} limit - Rows to read. A check-in has one panel per coach, so allow for them.
   */
  static async getByUser(userId, limit) {
    const weeklyCheckinCollection = new Collection(Collections.WEEKLY_CHECKIN, {
      createDocument: (src, opts) => new WeeklyCheckin(src.id, opts),
      query: (ref) => ref
        .where('user', '==', userId)
        .orderBy('submittedAt', 'desc')
        .limit(limit),
    });
    await weeklyCheckinCollection.fetch();
    return weeklyCheckinCollection;
  }
}

export default WeeklyCheckin;
